// apps/client/src/contexts/QueueContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { JobStatus, XeroSyncPhase, XeroSyncType } from '@avanzu/types';

// ============================================
// QUEUE TYPES
// ============================================

export enum QueueType {
  XERO_SYNC = 'xero_sync',
  PROJECT_GENERATION = 'project_generation',
}

// Xero Sync Job
export interface XeroSyncJob {
  queueType: QueueType.XERO_SYNC;
  jobId: string;
  syncType: XeroSyncType;
  progress: number;
  total: number;
  /** Local rows removed because the source document was deleted in Xero */
  deleted?: number;
  /** Live sub-step of a multi-phase sync (transactions only); null otherwise */
  phase?: XeroSyncPhase | null;
  status: JobStatus;
  errorMessage?: string;
  organisationId: string;
  pollInterval?: number;
}

// Project Generation Job
export interface ProjectGenerationJob {
  queueType: QueueType.PROJECT_GENERATION;
  jobId: string;
  status: JobStatus;
  projectId?: string;
  errorMessage?: string;
  organisationId: string;
  pollInterval?: number;
}

export type QueueJob = XeroSyncJob | ProjectGenerationJob;

// ============================================
// CONTEXT INTERFACE
// ============================================

interface QueueContextType {
  // Generic queue management
  jobs: Map<string, QueueJob>;
  addJob: (job: QueueJob) => void;
  updateJob: (jobId: string, updates: Partial<QueueJob>) => void;
  removeJob: (jobId: string) => void;

  // Convenience getters
  xeroSyncJob: XeroSyncJob | null;
  projectGenerationJob: ProjectGenerationJob | null;

  // Xero Sync specific (backward compatibility)
  startXeroSync: (job: Omit<XeroSyncJob, 'queueType'>) => void;
  updateXeroSyncProgress: (
    jobId: string,
    progress: number,
    total: number,
    deleted?: number,
    phase?: XeroSyncPhase | null
  ) => void;
  completeXeroSync: (jobId: string, success: boolean, errorMessage?: string) => void;
  clearXeroSync: () => void;
  cancelXeroSync: () => Promise<void>;

  // Project Generation specific
  startProjectGeneration: (job: Omit<ProjectGenerationJob, 'queueType'>) => void;
  completeProjectGeneration: (jobId: string, success: boolean, projectId?: string, errorMessage?: string) => void;
  clearProjectGeneration: () => void;
}

const QueueContext = createContext<QueueContextType | undefined>(undefined);

export const useQueue = () => {
  const context = useContext(QueueContext);
  if (!context) {
    throw new Error('useQueue must be used within a QueueProvider');
  }
  return context;
};

// Backward compatibility hooks
export const useXeroSync = () => {
  const context = useQueue();
  return {
    currentJob: context.xeroSyncJob,
    startSync: context.startXeroSync,
    updateProgress: context.updateXeroSyncProgress,
    completeSync: context.completeXeroSync,
    clearSync: context.clearXeroSync,
    cancelSync: context.cancelXeroSync,
  };
};

export const useChatGenerate = () => {
  const context = useQueue();
  return {
    currentJob: context.projectGenerationJob,
    startGeneration: context.startProjectGeneration,
    completeGeneration: context.completeProjectGeneration,
    clearGeneration: context.clearProjectGeneration,
  };
};

// ============================================
// PROVIDER
// ============================================

interface QueueProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'queueJobs';

export const QueueProvider: React.FC<QueueProviderProps> = ({ children }) => {
  const [jobs, setJobs] = useState<Map<string, QueueJob>>(new Map());
  const [initialized, setInitialized] = useState(false);
  const pollIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Persist jobs to sessionStorage
  const persistJobs = useCallback((jobsMap: Map<string, QueueJob>) => {
    const jobsArray = Array.from(jobsMap.values());
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(jobsArray));
  }, []);

  // Generic job management
  const addJob = useCallback((job: QueueJob) => {
    setJobs((prev) => {
      const next = new Map(prev);
      next.set(job.jobId, job);
      persistJobs(next);
      return next;
    });
  }, [persistJobs]);

  const updateJob = useCallback((jobId: string, updates: Partial<QueueJob>) => {
    setJobs((prev) => {
      const job = prev.get(jobId);
      if (!job) return prev;

      const next = new Map(prev);
      next.set(jobId, { ...job, ...updates } as QueueJob);
      persistJobs(next);
      return next;
    });
  }, [persistJobs]);

  const removeJob = useCallback((jobId: string) => {
    setJobs((prev) => {
      const next = new Map(prev);
      next.delete(jobId);
      persistJobs(next);
      return next;
    });

    // Clear polling interval
    const interval = pollIntervalsRef.current.get(jobId);
    if (interval) {
      clearInterval(interval);
      pollIntervalsRef.current.delete(jobId);
    }
  }, [persistJobs]);

  // Convenience getters
  const xeroSyncJob = Array.from(jobs.values()).find(
    (j): j is XeroSyncJob => j.queueType === QueueType.XERO_SYNC
  ) || null;

  const projectGenerationJob = Array.from(jobs.values()).find(
    (j): j is ProjectGenerationJob => j.queueType === QueueType.PROJECT_GENERATION
  ) || null;

  // ============================================
  // XERO SYNC SPECIFIC
  // ============================================

  const startXeroSync = useCallback((job: Omit<XeroSyncJob, 'queueType'>) => {
    addJob({ ...job, queueType: QueueType.XERO_SYNC });
  }, [addJob]);

  const updateXeroSyncProgress = useCallback((
    jobId: string,
    progress: number,
    total: number,
    deleted?: number,
    phase?: XeroSyncPhase | null
  ) => {
    updateJob(jobId, {
      progress,
      total,
      ...(deleted !== undefined && { deleted }),
      ...(phase !== undefined && { phase }),
      status: JobStatus.IN_PROGRESS,
    } as Partial<XeroSyncJob>);
  }, [updateJob]);

  const completeXeroSync = useCallback((jobId: string, success: boolean, errorMessage?: string) => {
    updateJob(jobId, {
      status: success ? JobStatus.COMPLETED : JobStatus.FAILED,
      errorMessage,
    } as Partial<XeroSyncJob>);

    // Auto-clear after 5 seconds
    setTimeout(() => {
      removeJob(jobId);
    }, 5000);
  }, [updateJob, removeJob]);

  const clearXeroSync = useCallback(() => {
    if (xeroSyncJob) {
      removeJob(xeroSyncJob.jobId);
    }
  }, [xeroSyncJob, removeJob]);

  const cancelXeroSync = useCallback(async () => {
    if (!xeroSyncJob) return;

    try {
      const { cancelXeroSync: cancelXeroSyncAPI } = await import('../services/xeroSync');
      await cancelXeroSyncAPI(xeroSyncJob.organisationId, xeroSyncJob.jobId);
      completeXeroSync(xeroSyncJob.jobId, false, 'Cancelled by user');
    } catch (error) {
      console.error('Error cancelling sync:', error);
      completeXeroSync(xeroSyncJob.jobId, false, 'Failed to cancel');
    }
  }, [xeroSyncJob, completeXeroSync]);

  // ============================================
  // PROJECT GENERATION SPECIFIC
  // ============================================

  const startProjectGeneration = useCallback((job: Omit<ProjectGenerationJob, 'queueType'>) => {
    addJob({ ...job, queueType: QueueType.PROJECT_GENERATION });
  }, [addJob]);

  const completeProjectGeneration = useCallback((
    jobId: string,
    success: boolean,
    projectId?: string,
    errorMessage?: string
  ) => {
    updateJob(jobId, {
      status: success ? JobStatus.COMPLETED : JobStatus.FAILED,
      projectId,
      errorMessage,
    } as Partial<ProjectGenerationJob>);
    // Don't auto-clear - user needs to click button or dismiss
  }, [updateJob]);

  const clearProjectGeneration = useCallback(() => {
    if (projectGenerationJob) {
      removeJob(projectGenerationJob.jobId);
    }
  }, [projectGenerationJob, removeJob]);

  // ============================================
  // RESTORE FROM SESSION STORAGE
  // ============================================

  useEffect(() => {
    const storedJobs = sessionStorage.getItem(STORAGE_KEY);
    if (storedJobs) {
      try {
        const jobsArray = JSON.parse(storedJobs) as QueueJob[];
        const restoredJobs = new Map<string, QueueJob>();

        jobsArray.forEach((job) => {
          // Only restore active or completed jobs
          if (job.queueType === QueueType.XERO_SYNC) {
            const xeroJob = job as XeroSyncJob;
            if (
              xeroJob.status === JobStatus.QUEUED ||
              xeroJob.status === JobStatus.IN_PROGRESS
            ) {
              restoredJobs.set(job.jobId, job);
            }
          } else if (job.queueType === QueueType.PROJECT_GENERATION) {
            const genJob = job as ProjectGenerationJob;
            if (
              genJob.status === JobStatus.QUEUED ||
              genJob.status === JobStatus.IN_PROGRESS ||
              genJob.status === JobStatus.COMPLETED
            ) {
              restoredJobs.set(job.jobId, job);
            }
          }
        });

        setJobs(restoredJobs);
      } catch (err) {
        console.error('Failed to restore queue jobs:', err);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    setInitialized(true);
  }, []);

  // ============================================
  // POLLING LOGIC
  // ============================================

  useEffect(() => {
    if (!initialized) return;

    jobs.forEach((job) => {
      const isActive =
        (job.queueType === QueueType.XERO_SYNC &&
         (job.status === JobStatus.QUEUED || job.status === JobStatus.IN_PROGRESS)) ||
        (job.queueType === QueueType.PROJECT_GENERATION &&
         (job.status === JobStatus.QUEUED || job.status === JobStatus.IN_PROGRESS));

      if (!isActive) {
        // Clear polling if job is complete
        const interval = pollIntervalsRef.current.get(job.jobId);
        if (interval) {
          clearInterval(interval);
          pollIntervalsRef.current.delete(job.jobId);
        }
        return;
      }

      // Start polling if not already polling
      if (!pollIntervalsRef.current.has(job.jobId)) {
        const pollInterval = job.pollInterval || 2000;

        const poll = async () => {
          try {
            if (job.queueType === QueueType.XERO_SYNC) {
              const xeroJob = job as XeroSyncJob;
              const { getXeroSyncProgress } = await import('../services/xeroSync');
              const result = await getXeroSyncProgress(xeroJob.organisationId, xeroJob.jobId);

              if (result.success) {
                const { job: progressJob } = result;
                updateXeroSyncProgress(
                  xeroJob.jobId,
                  progressJob.progress || 0,
                  progressJob.total || 0,
                  progressJob.deleted ?? undefined,
                  (progressJob.phase as XeroSyncPhase | null | undefined) ?? undefined
                );

                if (progressJob.status === JobStatus.COMPLETED) {
                  completeXeroSync(xeroJob.jobId, true);
                } else if (progressJob.status === JobStatus.FAILED) {
                  completeXeroSync(xeroJob.jobId, false, progressJob.errorMessage || 'Sync failed');
                }
              }
            } else if (job.queueType === QueueType.PROJECT_GENERATION) {
              const genJob = job as ProjectGenerationJob;
              const { getProjectGenerationProgress } = await import('../services/projectGeneration');
              const result = await getProjectGenerationProgress(genJob.organisationId, genJob.jobId);

              if (result.success) {
                const { job: progressJob } = result;
                updateJob(genJob.jobId, {
                  status: progressJob.status,
                  projectId: progressJob.projectId || undefined,
                  errorMessage: progressJob.errorMessage || undefined,
                });

                if (progressJob.status === JobStatus.COMPLETED) {
                  completeProjectGeneration(genJob.jobId, true, progressJob.projectId || undefined);
                } else if (progressJob.status === JobStatus.FAILED) {
                  completeProjectGeneration(genJob.jobId, false, undefined, progressJob.errorMessage || 'Generation failed');
                }
              }
            }
          } catch (err) {
            console.error(`Error polling job ${job.jobId}:`, err);
          }
        };

        const intervalId = setInterval(poll, pollInterval);
        pollIntervalsRef.current.set(job.jobId, intervalId);
      }
    });

    // Cleanup intervals for removed jobs
    pollIntervalsRef.current.forEach((interval, jobId) => {
      if (!jobs.has(jobId)) {
        clearInterval(interval);
        pollIntervalsRef.current.delete(jobId);
      }
    });
  }, [initialized, jobs, updateXeroSyncProgress, completeXeroSync, completeProjectGeneration, updateJob]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pollIntervalsRef.current.forEach((interval) => clearInterval(interval));
    };
  }, []);

  return (
    <QueueContext.Provider
      value={{
        jobs,
        addJob,
        updateJob,
        removeJob,
        xeroSyncJob,
        projectGenerationJob,
        startXeroSync,
        updateXeroSyncProgress,
        completeXeroSync,
        clearXeroSync,
        cancelXeroSync,
        startProjectGeneration,
        completeProjectGeneration,
        clearProjectGeneration,
      }}
    >
      {children}
    </QueueContext.Provider>
  );
};
